'use client';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Fab,
  Zoom,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { OrderDto } from 'desk-common';
import { useProducts } from '../../hooks/useProducts';
import { StateRenderer } from './OrderStateChip';
import { useRef, useState, useEffect, useCallback } from 'react';
import { OrderSummaryInfo } from './OrderSummaryInfo';
import { OrderSummaryTable } from './OrderSummaryTable';

type Props = {
  open: boolean;
  onClose: () => void;
  order?: OrderDto | null;
};

export default function OrderSummaryModal({ open, onClose, order }: Props) {
  const { data: products = [], isLoading, isFetching } = useProducts({});

  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollDown, setShowScrollDown] = useState(false);

  const checkScroll = useCallback(() => {
    if (contentRef.current) {
      const { scrollHeight, scrollTop, clientHeight } = contentRef.current;
      const isBottom = scrollHeight - scrollTop <= clientHeight + 5;
      const hasScroll = scrollHeight > clientHeight;
      setShowScrollDown(hasScroll && !isBottom);
    }
  }, [contentRef]);

  useEffect(() => {
    if (isLoading || isFetching || !contentRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isLoading, isFetching, checkScroll]);

  const handleScroll = () => {
    checkScroll();
  };

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  if (!order) {
    return null;
  }

  const isLoadingData = isLoading || isFetching;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: { position: 'relative' },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          py: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            Pedido #{order.id}
          </Typography>

          <StateRenderer value={order.state} />
        </Stack>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        ref={contentRef}
        onScroll={handleScroll}
        dividers
        sx={{
          px: 4,
          py: 4,
          maxHeight: '70vh',
          display: isLoadingData ? 'flex' : 'block',
          justifyContent: isLoadingData ? 'center' : 'initial',
          alignItems: isLoadingData ? 'center' : 'initial',
          minHeight: isLoadingData ? '300px' : 'auto',
        }}
      >
        {isLoadingData ? (
          <CircularProgress />
        ) : (
          <>
            <OrderSummaryInfo order={order} />
            <OrderSummaryTable order={order} products={products} />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 3,
          borderTop: '1px solid #eee',
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            px: 4,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Cerrar
        </Button>
      </DialogActions>

      <Zoom in={showScrollDown}>
        <Fab
          size="small"
          onClick={scrollToBottom}
          sx={{
            position: 'absolute',
            bottom: 105,
            right: 20,
            backgroundColor: '#ffffff',
            color: '#ff6b00',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: '#fff3ea',
            },
          }}
        >
          <KeyboardArrowDownIcon />
        </Fab>
      </Zoom>
    </Dialog>
  );
}
